import { useState, useEffect } from 'react';
import useSubstratePanelStore from '../stores/substratePanelStore';

const useDeviceType = () => {
  const activePanel = useSubstratePanelStore((s) => s.activePanel);
  const [deviceType, setDeviceType] = useState('desktop');
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  });

  useEffect(() => {
    const determineDeviceType = (width) => {
      if (width < 640) {
        return 'phone';
      } else if (width < 1024) {
        return 'tablet';
      } else {
        return 'desktop';
      }
    };

    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setScreenSize({ width, height });
      setDeviceType(determineDeviceType(width));
    };

    // Set initial values
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Helper functions for common breakpoint checks
  const isMobile = deviceType === 'phone';
  const isTablet = deviceType === 'tablet';
  const isDesktop = deviceType === 'desktop';
  const isMobileOrTablet = deviceType === 'phone' || deviceType === 'tablet';

  // Responsive utility functions
  const getResponsiveValue = (phoneValue, tabletValue, desktopValue) => {
    switch (deviceType) {
      case 'phone':
        return phoneValue;
      case 'tablet':
        return tabletValue || phoneValue;
      case 'desktop':
        return desktopValue || tabletValue || phoneValue;
      default:
        return desktopValue || tabletValue || phoneValue;
    }
  };

  const getResponsiveClasses = (phoneClasses, tabletClasses, desktopClasses) => {
    return getResponsiveValue(phoneClasses, tabletClasses, desktopClasses);
  };

  const getCardPadding = () => {
    return getResponsiveValue('p-2', 'p-3', 'p-4');
  };

  const getTextSize = (baseSize = 'base') => {
    const sizes = {
      xs: getResponsiveValue('text-xs', 'text-sm', 'text-sm'),
      sm: getResponsiveValue('text-sm', 'text-base', 'text-base'),
      base: getResponsiveValue('text-base', 'text-lg', 'text-lg'),
      lg: getResponsiveValue('text-lg', 'text-xl', 'text-xl'),
      xl: getResponsiveValue('text-xl', 'text-2xl', 'text-2xl')
    };
    
    return sizes[baseSize] || sizes.base;
  };

  const getSpacing = (size = 'md') => {
    const spacings = {
      xs: getResponsiveValue('gap-1', 'gap-2', 'gap-2'),
      sm: getResponsiveValue('gap-2', 'gap-3', 'gap-3'),
      md: getResponsiveValue('gap-3', 'gap-4', 'gap-4'),
      lg: getResponsiveValue('gap-4', 'gap-5', 'gap-6'),
      xl: getResponsiveValue('gap-5', 'gap-6', 'gap-8')
    };
    
    return spacings[size] || spacings.md;
  };

  const getMargin = (size = 'md') => {
    const margins = {
      xs: getResponsiveValue('m-1', 'm-2', 'm-2'),
      sm: getResponsiveValue('m-2', 'm-3', 'm-4'),
      md: getResponsiveValue('m-4', 'm-6', 'm-8'),
      lg: getResponsiveValue('m-6', 'm-8', 'm-12'),
      xl: getResponsiveValue('m-8', 'm-12', 'm-16')
    };
    
    return margins[size] || margins.md;
  };

  // Touch-specific helpers
  const isTouchDevice = () => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  };

  const shouldShowTouchOptimizedUI = () => {
    return isMobile || isTablet || isTouchDevice();
  };

  // Layout helpers — mirrors GoldenRatioContainer's panel-aware behaviour.
  // Fullscreen: golden-ratio inset at lg+. Panel open: flat container so cards
  // don't get starved next to the ~470px substrate panel/rail.
  // Consumers append `mx-auto`, so we use padding (not margin) to avoid clashes.
  const getContainerMaxWidth = () =>
    activePanel == null
      ? 'container px-4 sm:px-8 lg:px-[19.1%]'
      : 'container px-4 sm:px-6';

  const getGridCols = (baseCol = 1) => {
    return getResponsiveValue(
      `grid-cols-${baseCol}`, 
      `grid-cols-${Math.min(baseCol * 2, 4)}`, 
      `grid-cols-${Math.min(baseCol * 3, 6)}`
    );
  };

  return {
    deviceType,
    screenSize,
    
    // Boolean helpers
    isMobile,
    isTablet,  
    isDesktop,
    isMobileOrTablet,
    
    // Responsive utilities
    getResponsiveValue,
    getResponsiveClasses,
    getCardPadding,
    getTextSize,
    getSpacing,
    getMargin,
    getContainerMaxWidth,
    getGridCols,
    
    // Touch utilities
    isTouchDevice,
    shouldShowTouchOptimizedUI
  };
};

export default useDeviceType;